// @workflow assistant.send-message
/**
 * Pure helpers for the findings of an AVA Inspect tag: review filters, the corrosion / pitting
 * category colours of the markers, and where a finding sits on the live 360 view.
 *
 * No Vue, no store, no '@/' imports: these run under `node --test` as well as in the bundle.
 */
import { pixelToImageYawPitch, imageToViewer } from './panoProjection.js';

export const FINDING_FILTER = Object.freeze({
  REVIEW: 'review',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  ALL: 'all',
});

export const FINDING_FILTERS = Object.freeze([
  { value: FINDING_FILTER.REVIEW, label: 'To review' },
  { value: FINDING_FILTER.ACCEPTED, label: 'Accepted' },
  { value: FINDING_FILTER.REJECTED, label: 'Rejected' },
  { value: FINDING_FILTER.ALL, label: 'All' },
]);

/** How findings show on the 360 view: category diamonds, or their corrosion outlines. */
export const FINDING_DISPLAY = Object.freeze({ MARKERS: 'markers', POLYGONS: 'polygons' });

export const FINDING_DISPLAYS = Object.freeze([
  { value: FINDING_DISPLAY.MARKERS, label: 'Markers', icon: 'mdi-rhombus', hint: 'Show findings as category markers' },
  { value: FINDING_DISPLAY.POLYGONS, label: 'Polygons', icon: 'mdi-vector-polygon', hint: 'Show the corrosion outline of every finding' },
]);

const APPROVED = 'Accepted';
const REJECTED = 'Rejected';

/**
 * Corrosion / pitting category (Cat A - D) as the markers and chips show it: green, yellow, orange,
 * red, from light surface corrosion to severe corrosion with evident substrate loss. `ink` is the
 * readable text colour on that fill.
 */
export const CATEGORY_STYLES = Object.freeze({
  A: { key: 'A', label: 'Cat A', color: '#2e9d4f', ink: '#ffffff', description: 'Light surface corrosion & scale' },
  B: { key: 'B', label: 'Cat B', color: '#f2c200', ink: '#3a2e00', description: 'Moderate corrosion & adherent scale' },
  C: { key: 'C', label: 'Cat C', color: '#f07c12', ink: '#ffffff', description: 'Heavy corrosion, severe scale, pitting' },
  D: { key: 'D', label: 'Cat D', color: '#d32f2f', ink: '#ffffff', description: 'Severe corrosion, evident substrate loss' },
});

export const UNCATEGORISED_STYLE = Object.freeze({ key: null, label: 'No category', color: '#8a8f98', ink: '#ffffff', description: '' });

/** Fallback when neither the finding nor the scan knows the image size (AI_INSPECT/GET_IMAGES uses the same). */
export const DEFAULT_IMAGE_SIZE = Object.freeze({ width: 4057, height: 2028 });

/** "CatB", "Cat B", "cat_b", "B" -> "B"; anything else -> null. */
export function categoryOf(value) {
  const normalized = String(value || '').replace(/[\s_-]/g, '').toUpperCase();
  const match = /^(?:CAT)?([ABCD])$/.exec(normalized);
  return match ? match[1] : null;
}

export function categoryStyle(value) {
  return CATEGORY_STYLES[categoryOf(value)] || UNCATEGORISED_STYLE;
}

/** 'review' (not reviewed yet), 'accepted' or 'rejected'. */
export function reviewStatusOf(finding) {
  if (finding?.approvalStatus === APPROVED) return FINDING_FILTER.ACCEPTED;
  if (finding?.approvalStatus === REJECTED) return FINDING_FILTER.REJECTED;
  return FINDING_FILTER.REVIEW;
}

export function filterFindings(findings, filter) {
  const list = Array.isArray(findings) ? findings : [];
  return filter === FINDING_FILTER.ALL ? list : list.filter((f) => reviewStatusOf(f) === filter);
}

/** Category filter key of findings without a category (A - D use their letter). */
export const NO_CATEGORY = 'none';

/** 'A' - 'D', or NO_CATEGORY. */
export function categoryKeyOf(finding) {
  return categoryOf(finding?.corrosionCategory) || NO_CATEGORY;
}

/** Severity class ids as the analysis stores them, with their labels. */
export const SEVERITY_LABELS = Object.freeze({ 0: 'Light', 1: 'Medium', 2: 'Heavy', 3: 'Severe' });

/**
 * The finding view-model with the grades and text of a saved anomaly row (AnomalyInsightVM, the shape
 * the edit panel saves): type -> defectType, pittingCategory -> corrosionCategory, coating breakdown,
 * severity, description and location. Its outline, views and review status are untouched.
 */
export function findingFromAnomaly(finding, row) {
  if (!row) return finding;
  const text = (value) => (value == null || value === '' ? null : String(value));
  return {
    ...finding,
    defectType: text(row.type),
    corrosionCategory: text(row.pittingCategory),
    coatingBreakdown: text(row.coatingBreakdown),
    severity: text(row.severity),
    description: text(row.description),
    locationDescription: text(row.locationDescription),
  };
}

/**
 * The finding picked in the Findings tab (AVA_FINDINGS `selected`) as the assistant's message context names it, so
 * "that finding" means the one the user picked: `{ id, name, tagId }`, the AI finding's id (the one review_finding and
 * list_ai_findings take), its label as AVA shows it (MAS-MER-AI-244, null when unknown) and the AVA Inspect tag whose
 * findings list it is in (it counts only while that tag is open). Null when none is picked.
 */
export function findingSubject(finding, tagId) {
  if (!finding || finding.id == null || finding.id === '') return null;
  return { id: String(finding.id), name: finding.friendlyId ? String(finding.friendlyId) : null, tagId: tagId == null ? null : String(tagId) };
}

/** Findings in any of the category keys; an empty selection keeps every finding. */
export function filterByCategories(findings, keys) {
  const list = Array.isArray(findings) ? findings : [];
  return keys && keys.length ? list.filter((f) => keys.includes(categoryKeyOf(f))) : list;
}

/** { A, B, C, D, none } */
export function countByCategory(findings) {
  const counts = { A: 0, B: 0, C: 0, D: 0, [NO_CATEGORY]: 0 };
  (Array.isArray(findings) ? findings : []).forEach((f) => { counts[categoryKeyOf(f)] += 1; });
  return counts;
}

/** { review, accepted, rejected, all } */
export function countByFilter(findings) {
  const counts = { review: 0, accepted: 0, rejected: 0, all: 0 };
  (Array.isArray(findings) ? findings : []).forEach((f) => {
    counts[reviewStatusOf(f)] += 1;
    counts.all += 1;
  });
  return counts;
}

/**
 * Where the master tag of an AVA Inspect tag comes from, as the Info tab's Master Tag field reads it:
 * its saved master tag (`masterTagId` / `masterTag`), else its linked tags, of which only those with
 * isMaster count. `explicit` says which: { ids, explicit }.
 */
export function masterTagIdsOf(element) {
  const explicitIds = [...new Set([element?.masterTagId, element?.masterTag?.id].filter(Boolean))];
  if (explicitIds.length) return { ids: explicitIds, explicit: true };
  return { ids: [...new Set((element?.linkedTagsList || []).filter(Boolean))], explicit: false };
}

/**
 * Every place a finding shows: its own scan, then the other scans it was seen on (views). Each
 * placement is { findingId, imageId, imageName, centerX, centerY, outline, sourceImageWidth, sourceImageHeight }.
 */
export function placementsOf(finding) {
  if (!finding) return [];
  return [finding, ...(finding.views || [])]
    .filter((p) => p && p.imageId)
    .map((p) => ({
      findingId: finding.id,
      imageId: p.imageId,
      imageName: p.imageName,
      centerX: p.centerX,
      centerY: p.centerY,
      outline: p.outline || [],
      sourceImageWidth: p.sourceImageWidth,
      sourceImageHeight: p.sourceImageHeight,
    }));
}

/** The pixel size the placement's coordinates are in: recorded by the analysis, else the scan's, else the default. */
export function sourceSizeOf(placement, image) {
  const width = Number(placement?.sourceImageWidth) || Number(image?.width) || DEFAULT_IMAGE_SIZE.width;
  const height = Number(placement?.sourceImageHeight) || Number(image?.height) || DEFAULT_IMAGE_SIZE.height;
  return { width, height };
}

/**
 * A pixel of the source image as the 360 viewer's yaw/pitch. `rotation` is horizonRotation() of the
 * live viewer (levelled scans), the same pose snap outlines are stored against.
 */
export function pixelToViewer(x, y, size, rotation) {
  return imageToViewer(pixelToImageYawPitch(Number(x), Number(y), size.width, size.height), rotation);
}

export function placementCenter(placement, size, rotation) {
  return pixelToViewer(placement.centerX, placement.centerY, size, rotation);
}

/** The corrosion outline in viewer yaw/pitch; empty when the finding has no usable outline. */
export function placementOutline(placement, size, rotation) {
  const outline = placement?.outline || [];
  return outline.length >= 3 ? outline.map((p) => pixelToViewer(p.x, p.y, size, rotation)) : [];
}

/**
 * Where a popup card goes next to a marker at (x, y) inside a viewer of width x height: to the right
 * of the marker, or to its left when it would not fit, vertically centred on it, always kept inside
 * the viewer with `margin` to spare. Returns the card's top-left corner.
 */
export function popupPosition({ x, y, width, height, cardWidth, cardHeight, gap = 18, margin = 8 }) {
  const fitsRight = x + gap + cardWidth <= width - margin;
  const left = fitsRight ? x + gap : x - gap - cardWidth;
  const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
  return {
    left: clamp(left, margin, Math.max(margin, width - margin - cardWidth)),
    top: clamp(y - cardHeight / 2, margin, Math.max(margin, height - margin - cardHeight)),
    side: fitsRight ? 'right' : 'left',
  };
}

/** Whether a 3D point was resolved for the finding. */
export function hasPoint3d(finding) {
  const p = finding?.point3d;
  return !!p && ['x', 'y', 'z'].every((k) => Number.isFinite(Number(p[k]))) && !(Number(p.x) === 0 && Number(p.y) === 0 && Number(p.z) === 0);
}
